import { useState, useEffect, useMemo } from 'react';
import { useOrganizations } from '@/domains/accounts/services/accounts';
import { useAddL2VPNCircuitToOrganization } from '@/domains/accounts/services/accounts';
import { AddL2VPNToAccountDetails, MAX_VLAN, MIN_VLAN } from '@/shared/types/l2vpnTypes';
import { useStarlinkL2VPNCircuits } from '@/domains/l2vpns/services/l2vpn-circuits';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Loader2, Search, User, X, Plus, Trash2 } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

export interface CircuitMapping {
  id: string;
  circuitId: string;
  popName: string;
  circuitName: string;
  customerVlanStr: string; // Comma-separated or range, e.g., "200, 400-410, 800"
  serviceVlanStr: string; // Comma-separated or range, e.g., "200, 400-410, 800"
}

interface AddL2VPNCircuitToAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  selectedRegionCode: string;
}

export default function AddL2VPNCircuitToAccountDialog({
  open,
  onOpenChange,
  onSuccess,
  selectedRegionCode,
}: AddL2VPNCircuitToAccountDialogProps) {
  const [mappings, setMappings] = useState<CircuitMapping[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [searchAccountTerm, setSearchAccountTerm] = useState('');

  // Fetch Starlink L2VPN circuits
  const { data: starlinkCircuits, isLoading: isLoadingCircuits } = useStarlinkL2VPNCircuits(
    open,
    selectedRegionCode
  );

  // Fetch accounts
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);
  const addL2VPNCircuitToOrganization = useAddL2VPNCircuitToOrganization();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setMappings([]);
      setSelectedOrganizationId('');
      setSearchAccountTerm('');
    }
  }, [open]);

  // Filter accounts based on search term
  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];
    if (!searchAccountTerm) return organizations;

    const searchLower = searchAccountTerm.toLowerCase();
    return organizations.filter(organization =>
      organization.name?.toLowerCase().includes(searchLower) ||
      organization.organizationId?.toLowerCase().includes(searchLower)
    );
  }, [organizations, searchAccountTerm]);

  // Get available circuits for select (exclude already selected ones)
  const getAvailableCircuits = (currentMappingId: string) => {
    if (!starlinkCircuits) return [];
    return starlinkCircuits.filter(circuit => {
      // Include if not selected in any other mapping
      return !mappings.some(m => m.id !== currentMappingId && m.circuitId === circuit.circuitId);
    });
  };

  // Parse VLAN string (comma-separated and ranges) into array of numbers
  const parseVlanString = (vlanStr: string): { vlans: number[]; error?: string } => {
    if (!vlanStr.trim()) {
      return { vlans: [] };
    }

    const vlans: number[] = [];
    const parts = vlanStr.split(',').map(part => part.trim()).filter(part => part.length > 0);

    for (const part of parts) {
      // Check if it's a range (e.g., "400-410")
      if (part.includes('-')) {
        const rangeParts = part.split('-').map(p => p.trim());
        if (rangeParts.length !== 2) {
          return { vlans: [], error: `Invalid range format: "${part}". Use format "start-end" (e.g., "400-410")` };
        }

        const start = Number.parseInt(rangeParts[0], 10);
        const end = Number.parseInt(rangeParts[1], 10);

        if (Number.isNaN(start) || Number.isNaN(end)) {
          return { vlans: [], error: `Invalid range values: "${part}". Both start and end must be numbers.` };
        }

        if (start < MIN_VLAN || start > MAX_VLAN || end < MIN_VLAN || end > MAX_VLAN) {
          return { vlans: [], error: `Range "${part}" contains values outside ${MIN_VLAN}-${MAX_VLAN}` };
        }

        if (start > end) {
          return { vlans: [], error: `Range "${part}" has start value greater than end value` };
        }

        // Expand range
        for (let vlan = start; vlan <= end; vlan++) {
          vlans.push(vlan);
        }
      } else {
        // Single VLAN value
        const vlan = Number.parseInt(part, 10);
        if (Number.isNaN(vlan)) {
          return { vlans: [], error: `Invalid VLAN value: "${part}". Must be a number.` };
        }

        if (vlan < MIN_VLAN || vlan > MAX_VLAN) {
          return { vlans: [], error: `VLAN ${vlan} must be between ${MIN_VLAN}-${MAX_VLAN}` };
        }

        vlans.push(vlan);
      }
    }

    return { vlans };
  };

  // Validate VLANs across all mappings
  const validateVlans = (inputVlans: string[]): { isValid: boolean; error?: string; parsedVlans?: number[][] } => {
    const parsedVlansArrays: number[][] = [];

    for (let i = 0; i < inputVlans.length; i++) {
      const inputVlan = inputVlans[i];
      const parseResult = parseVlanString(inputVlan);

      if (parseResult.error) {
        return { isValid: false, error: parseResult.error };
      }

      const vlanNumbers = parseResult.vlans;
      parsedVlansArrays.push(vlanNumbers);
    }

    return { isValid: true, parsedVlans: parsedVlansArrays };
  };
  // Validate mapping uniqueness
  const validateMapping = (): { isValid: boolean; error?: string; parsedData?: { customerVlans: number[][]; serviceVlans: number[][] } } => {
    if (mappings.length === 0) {
      return { isValid: false, error: 'Please add at least one mapping' };
    }

    for (const mapping of mappings) {
      if (!mapping.circuitId) {
        return { isValid: false, error: 'Please select a circuit for all mappings' };
      } else if (!mapping.circuitName.trim()) {
        return { isValid: false, error: 'Please enter a circuit name for all mappings' };
      } else if (!mapping.customerVlanStr.trim()) {
        return { isValid: false, error: `Please enter customer VLANs for all mappings (${MIN_VLAN}-${MAX_VLAN})` };
      } else if (!mapping.serviceVlanStr.trim()) {
        return { isValid: false, error: `Please enter service VLANs for all mappings (${MIN_VLAN}-${MAX_VLAN})` };
      }
    }

    const customerVlanValidation = validateVlans(mappings.map(mapping => mapping.customerVlanStr));
    if (!customerVlanValidation.isValid) {
      return { isValid: false, error: customerVlanValidation.error };
    }

    const serviceVlanValidation = validateVlans(mappings.map(mapping => mapping.serviceVlanStr));
    if (!serviceVlanValidation.isValid) {
      return { isValid: false, error: serviceVlanValidation.error };
    }

    return {
      isValid: true,
      parsedData: {
        customerVlans: customerVlanValidation.parsedVlans || [],
        serviceVlans: serviceVlanValidation.parsedVlans || []
      }
    };
  };

  const handleAddMapping = () => {
    const newMapping: CircuitMapping = {
      id: `mapping-${Date.now()}`,
      circuitId: '',
      popName: '',
      circuitName: '',
      customerVlanStr: '',
      serviceVlanStr: '',
    };
    setMappings(prev => [...prev, newMapping]);
  };

  const handleRemoveMapping = (id: string) => {
    setMappings(prev => prev.filter(m => m.id !== id));
  };

  const handleCircuitChange = (mappingId: string, circuitId: string) => {
    const circuit = starlinkCircuits?.find(c => c.circuitId === circuitId);
    setMappings(prev => prev.map(m => {
      if (m.id === mappingId) {
        return {
          ...m,
          circuitId: circuitId,
          popName: circuit?.popName || '',
        };
      }
      return m;
    }));
  };

  // Filter input to only allow numbers, commas, hyphens, and spaces
  const filterVlanInput = (value: string): string => {
    // Remove any characters that are not digits, commas, hyphens, or spaces
    return value.replace(/[^0-9,\-\s]/g, '');
  };

  const handleMappingFieldChange = (mappingId: string, field: keyof CircuitMapping, value: string) => {
    setMappings(prev => prev.map(m => {
      if (m.id === mappingId) {
        // Always apply input filter for VLAN fields to catch any bypassed input
        const filteredValue = (field === 'customerVlanStr' || field === 'serviceVlanStr')
          ? filterVlanInput(value)
          : value;
        return { ...m, [field]: filteredValue };
      }
      return m;
    }));
  };

  const handleSubmit = async () => {
    if (!selectedOrganizationId) {
      showError(ERROR.L2VPN.NO_ACCOUNT_SELECTED);
      return;
    }

    const validation = validateMapping();
    if (!validation.isValid) {
      showError(validation.error || 'Validation failed');
      return;
    }

    try {
      // Convert mappings to API format using parsed VLANs
      const circuits: AddL2VPNToAccountDetails[] = mappings.map((mapping) => {
        return {
          circuitId: mapping.circuitId,
          popName: mapping.popName,
          circuitName: mapping.circuitName,
          // Convert number arrays to string arrays as expected by API
          customerVlanRange: mapping.customerVlanStr,
          serviceVlanRange: mapping.serviceVlanStr,
        }
      });

      await addL2VPNCircuitToOrganization.mutateAsync({
        organizationId: selectedOrganizationId,
        circuits,
      });

      showSuccess(SUCCESS.L2VPN.ADDED_TO_ACCOUNT);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.L2VPN.ADD_TO_ACCOUNT);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setMappings([]);
      setSelectedOrganizationId('');
      setSearchAccountTerm('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>L2VPN</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Instructions */}
          <div className="space-y-2 text-sm">
            <p>Create mappings of circuit IDs to a set of VLANs.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Each Customer and Service VLAN mapping must have unique VLANs from {MIN_VLAN}-{MAX_VLAN}.</li>
              <li>Customer and Service VLANs can be only Ranges. For example: 400-410, 800-810.</li>
            </ul>
          </div>

          {/* Circuit Mappings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Circuit Mappings</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMapping}
                disabled={isLoadingCircuits || !starlinkCircuits || starlinkCircuits.length === 0 || mappings.length == starlinkCircuits.length}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Mapping
              </Button>
            </div>

            {isLoadingCircuits ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : mappings.length === 0 ? (
              <div className="text-center py-8">
                <p>No mappings added. Click "Add Mapping" to create one.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mappings.map((mapping) => {
                  const availableCircuits = getAvailableCircuits(mapping.id);
                  return (
                    <div key={mapping.id} className="flex items-center gap-4 p-4 border rounded-lg bg-card">
                      <div className="flex-1 grid grid-cols-4 gap-4">
                        {/* Circuit Select */}
                        <div className="space-y-2">
                          <Label htmlFor={`circuit-${mapping.id}`} className="text-xs">
                            Circuit ID
                          </Label>
                          <Select
                            value={mapping.circuitId}
                            onValueChange={(value) => handleCircuitChange(mapping.id, value)}
                          >
                            <SelectTrigger id={`circuit-${mapping.id}`}>
                              <SelectValue placeholder="Select circuit" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCircuits.length === 0 ? (
                                <div className="px-2 py-1.5 text-sm">
                                  No circuits available
                                </div>
                              ) : (
                                availableCircuits.map((circuit) => (
                                  <SelectItem key={circuit.circuitId} value={circuit.circuitId}>
                                    {circuit.circuitId} ({circuit.popName})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Circuit Name Input */}
                        <div className="space-y-2">
                          <Label htmlFor={`circuitName-${mapping.id}`} className="text-xs">
                            Circuit Name
                          </Label>
                          <Input
                            id={`circuitName-${mapping.id}`}
                            value={mapping.circuitName}
                            onChange={(e) => handleMappingFieldChange(mapping.id, 'circuitName', e.target.value)}
                            placeholder="Enter circuit name"
                          />
                        </div>

                        {/* Customer VLANs Input */}
                        <div className="space-y-2">
                          <Label htmlFor={`customerVlans-${mapping.id}`} className="text-xs">
                            Customer VLANs ({MIN_VLAN}-{MAX_VLAN})
                          </Label>
                          <Input
                            id={`customerVlans-${mapping.id}`}
                            value={filterVlanInput(mapping.customerVlanStr)}
                            onChange={(e) => {
                              const filtered = filterVlanInput(e.target.value);
                              handleMappingFieldChange(mapping.id, 'customerVlanStr', filtered);
                            }}
                            placeholder="e.g. 400-410 (Range)"
                            type="text"
                            inputMode="text"
                            pattern="[0-9]*-[0-9]*"
                          />
                        </div>

                        {/* Service VLANs Input */}
                        <div className="space-y-2">
                          <Label htmlFor={`serviceVlan-${mapping.id}`} className="text-xs">
                            Service VLANs ({MIN_VLAN}-{MAX_VLAN})
                          </Label>
                          <Input
                            id={`serviceVlan-${mapping.id}`}
                            value={filterVlanInput(mapping.serviceVlanStr)}
                            onChange={(e) => {
                              const filtered = filterVlanInput(e.target.value);
                              handleMappingFieldChange(mapping.id, 'serviceVlanStr', filtered);
                            }}
                            placeholder="e.g. 400-410 (Range)"
                            type="text"
                            inputMode="text"
                            pattern="[0-9]*-[0-9]*"
                          />
                        </div>
                      </div>
                      {/* Remove Button */}
                      <div className="flex items-center justify-end">
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => handleRemoveMapping(mapping.id)}
                          className="h-10 w-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Account Selection Section */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Select Account</h4>
            {isLoadingOrganizations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (organizations?.length ?? 0) === 0 ? (
              <EmptyView
                icon={User}
                size="small"
                title="No Accounts Available"
                description="There are no accounts available."
              />
            ) : (
              <div className="space-y-2">
                {/* Account Search Input */}
                <div className="relative">
                  <SearchBar value={searchAccountTerm} onChange={(value) => setSearchAccountTerm(value)} placeholder="Search accounts..." />
                  {searchAccountTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSearchAccountTerm('')}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {filteredOrganizations.length > 0 ? (
                  <div className="max-h-[200px] overflow-y-auto border rounded-md p-2">
                    <div className="space-y-2">
                      {filteredOrganizations.map((organization) => (
                        <div key={organization.organizationId} className="flex items-center space-x-2 p-2 hover:bg-muted rounded">
                          <input
                            type="radio"
                            id={`organization-${organization.organizationId}`}
                            name="organization"
                            value={organization.organizationId}
                            checked={selectedOrganizationId === organization.organizationId}
                            onChange={(e) => setSelectedOrganizationId(e.target.value)}
                            className="h-4 w-4"
                          />
                          <Label
                            htmlFor={`organization-${organization.organizationId}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            <div className="flex flex-col">
                              <span>{organization.name}</span>
                              <span className="text-xs">ID: {organization.organizationId}</span>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyView
                    icon={Search}
                    size="small"
                    title="No Accounts Found"
                    description={`No accounts match "${searchAccountTerm}". Try adjusting your search.`}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleDialogClose(false)}
            disabled={addL2VPNCircuitToOrganization.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addL2VPNCircuitToOrganization.isPending || !selectedOrganizationId || !validateMapping().isValid}
          >
            {addL2VPNCircuitToOrganization.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add to Selected Account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
